import React, { useEffect, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { toast } from "sonner";
import { PageHeader, Card, Empty } from "@/components/ui-bits";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Pencil, Trash2, Users, Search } from "lucide-react";

const EMPTY = { route_name: "", route_code: "", description: "", driver_id: "", customer_ids: [], status: true };

export default function RoutesPage() {
  const [list, setList] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [custSearch, setCustSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  function load() {
    api.get("/routes").then(r => setList(r.data));
    api.get("/drivers").then(r => setDrivers(r.data));
    api.get("/customers").then(r => setCustomers(r.data));
  }
  useEffect(() => { load(); }, []);

  function openCreate() { setEditing(null); setForm(EMPTY); setCustSearch(""); setOpen(true); }
  function openEdit(r) {
    setEditing(r);
    setForm({
      ...EMPTY,
      ...r,
      driver_id: r.driver_id || "",
      customer_ids: r.customer_ids || [],
    });
    setCustSearch("");
    setOpen(true);
  }

  function toggleCustomer(cid) {
    const ids = form.customer_ids || [];
    if (ids.includes(cid)) {
      setForm({ ...form, customer_ids: ids.filter(id => id !== cid) });
    } else {
      setForm({ ...form, customer_ids: [...ids, cid] });
    }
  }

  function selectAllCustomers() {
    const filtered = filteredCustomers.map(c => c.id);
    const allSelected = filtered.every(id => (form.customer_ids || []).includes(id));
    if (allSelected) {
      setForm({ ...form, customer_ids: (form.customer_ids || []).filter(id => !filtered.includes(id)) });
    } else {
      const merged = Array.from(new Set([...(form.customer_ids || []), ...filtered]));
      setForm({ ...form, customer_ids: merged });
    }
  }

  async function save() {
    try {
      const payload = { ...form, driver_id: form.driver_id || null };
      if (editing) await api.put(`/routes/${editing.id}`, payload);
      else await api.post("/routes", payload);
      toast.success(editing ? "Route updated" : "Route created");
      setOpen(false); load();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  }

  async function remove(r) {
    if (!window.confirm(`Delete ${r.route_name}?`)) return;
    await api.delete(`/routes/${r.id}`);
    toast.success("Deleted"); load();
  }

  const filteredCustomers = customers.filter(c =>
    (c.business_name || "").toLowerCase().includes(custSearch.toLowerCase()) ||
    (c.customer_name || "").toLowerCase().includes(custSearch.toLowerCase()) ||
    (c.address || "").toLowerCase().includes(custSearch.toLowerCase())
  );

  return (
    <div data-testid="routes-page">
      <PageHeader
        eyebrow="Logistics"
        title="Routes"
        description="Delivery routes assigned to drivers and customers."
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button onClick={openCreate} className="bg-cobalt-500 hover:bg-cobalt-700 text-white" data-testid="route-create-btn"><Plus size={16} className="mr-1"/>New Route</Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col overflow-hidden">
              <DialogHeader><DialogTitle>{editing ? "Edit Route" : "New Route"}</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-4 overflow-y-auto pr-1 max-h-[65vh]">
                <div><Label>Route Name</Label><Input data-testid="route-name" value={form.route_name} onChange={e=>setForm({...form, route_name: e.target.value})}/></div>
                <div><Label>Route Code</Label><Input data-testid="route-code" value={form.route_code} onChange={e=>setForm({...form, route_code: e.target.value})}/></div>
                <div className="col-span-2">
                  <Label>Assigned Driver</Label>
                  <Select value={form.driver_id || "none"} onValueChange={(v)=>setForm({...form, driver_id: v === "none" ? "" : v})}>
                    <SelectTrigger><SelectValue placeholder="Unassigned"/></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Unassigned</SelectItem>
                      {drivers.map(d => <SelectItem key={d.id} value={d.id}>{d.name} ({d.username})</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>

                {/* Add / Select Customers Section */}
                <div className="col-span-2 space-y-2 border border-slate-200 rounded-lg p-3 bg-slate-50">
                  <div className="flex items-center justify-between">
                    <Label className="font-semibold text-slate-800 flex items-center gap-1.5">
                      <Users size={15} className="text-cobalt-600" />
                      Select Customers for this Route ({form.customer_ids?.length || 0} selected)
                    </Label>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-7 text-xs text-cobalt-600 hover:text-cobalt-800"
                      onClick={selectAllCustomers}
                    >
                      Toggle All Visible
                    </Button>
                  </div>

                  <div className="relative">
                    <Search size={14} className="absolute left-2.5 top-2.5 text-slate-400" />
                    <Input
                      placeholder="Search customers by name or location..."
                      value={custSearch}
                      onChange={e => setCustSearch(e.target.value)}
                      className="pl-8 bg-white h-9 text-xs"
                    />
                  </div>

                  <div className="max-h-48 overflow-y-auto border border-slate-200 rounded-md bg-white divide-y divide-slate-100">
                    {filteredCustomers.length === 0 ? (
                      <div className="p-4 text-center text-xs text-slate-500">No customers found</div>
                    ) : (
                      filteredCustomers.map(c => {
                        const checked = (form.customer_ids || []).includes(c.id);
                        return (
                          <label
                            key={c.id}
                            className={`flex items-center justify-between px-3 py-2 text-xs cursor-pointer hover:bg-slate-50 transition-colors ${
                              checked ? "bg-cobalt-50/60" : ""
                            }`}
                          >
                            <div className="flex items-center gap-2.5">
                              <Checkbox
                                checked={checked}
                                onCheckedChange={() => toggleCustomer(c.id)}
                              />
                              <div>
                                <div className="font-medium text-slate-800">{c.business_name} ({c.customer_name})</div>
                                <div className="text-[11px] text-slate-500">{c.address || "No address specified"}</div>
                              </div>
                            </div>
                            <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 font-mono text-slate-600">
                              {c.business_type}
                            </span>
                          </label>
                        );
                      })
                    )}
                  </div>
                </div>

                <div className="col-span-2"><Label>Description</Label><Textarea rows={2} value={form.description} onChange={e=>setForm({...form, description: e.target.value})}/></div>
              </div>
              <DialogFooter className="mt-2">
                <Button variant="outline" onClick={()=>setOpen(false)}>Cancel</Button>
                <Button onClick={save} className="bg-cobalt-500 hover:bg-cobalt-700" data-testid="route-save-btn">Save</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        }
      />

      <Card padding="p-0">
        {list.length === 0 ? <div className="p-8"><Empty title="No routes yet"/></div> : (
          <div className="overflow-x-auto">
            <table className="ddms-table" data-testid="routes-table">
              <thead><tr><th>Route Name</th><th>Code</th><th>Driver</th><th>Assigned Customers</th><th>Description</th><th className="num">Actions</th></tr></thead>
              <tbody>
                {list.map(r => (
                  <tr key={r.id}>
                    <td className="font-medium">{r.route_name}</td>
                    <td className="font-mono text-xs">{r.route_code}</td>
                    <td>{r.driver_name || <span className="text-slate-400">Unassigned</span>}</td>
                    <td>
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className="px-2 py-0.5 rounded-full bg-cobalt-50 text-cobalt-700 text-xs font-semibold">
                          {r.customers_count || 0} Customers
                        </span>
                        {r.customer_names && r.customer_names.length > 0 && (
                          <span className="text-[11px] text-slate-500 max-w-[200px] truncate" title={r.customer_names.join(", ")}>
                            ({r.customer_names.slice(0, 2).join(", ")}{r.customer_names.length > 2 ? ` +${r.customer_names.length - 2} more` : ""})
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="text-slate-500">{r.description}</td>
                    <td className="num">
                      <Button size="sm" variant="outline" onClick={()=>openEdit(r)} className="mr-2"><Pencil size={12}/></Button>
                      <Button size="sm" variant="outline" onClick={()=>remove(r)} className="text-rose-600 border-rose-200"><Trash2 size={12}/></Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
